import { lazy } from 'react';
import { z } from 'zod';
import { toDataState, useMetricQuery } from '../_sdk/hooks';
import { decimalsSchema, metricBinding, rangeSchema, unitSchema } from '../_sdk/schema';
import { defineWidget } from '../_sdk/types';

/** Single value + delta + threshold colouring (PRD VS-042, TechSpec §9.3). */

export const PropsSchema = z.object({
  ...metricBinding,
  unit: unitSchema,
  decimals: decimalsSchema,
  showDelta: z.boolean().default(true),
  /** Window of the delta: the value now against the value this long ago. */
  deltaRange: rangeSchema.default('24h'),
  thresholds: z
    .object({
      warn: z.number().optional(),
      critical: z.number().optional(),
      direction: z.enum(['above', 'below']).default('above'),
    })
    .optional(),
});

export type StatTileProps = z.output<typeof PropsSchema>;

export interface StatTileData {
  value: number;
  /** The oldest bucket of the delta window. */
  previous?: number | undefined;
  ts: Date;
}

export type Level = 'ok' | 'warn' | 'critical';

export function levelOf(value: number, t: StatTileProps['thresholds']): Level {
  if (!t) return 'ok';
  const past = (limit: number | undefined) =>
    limit !== undefined && (t.direction === 'below' ? value <= limit : value >= limit);
  if (past(t.critical)) return 'critical';
  if (past(t.warn)) return 'warn';
  return 'ok';
}

export default defineWidget<StatTileProps, StatTileData | null>({
  type: 'stat-tile',
  category: 'metric',
  defaultSize: { w: 3, h: 2 },
  minSize: { w: 2, h: 2 },
  propsSchema: PropsSchema,
  defaults: { decimals: 0, showDelta: true, deltaRange: '24h', tags: {} },
  sourceIds: (p) => [p.sourceId],
  useData: (p) =>
    toDataState(
      useMetricQuery({
        sourceId: p.sourceId,
        name: p.metricName,
        tags: p.tags,
        range: p.deltaRange,
        agg: 'last',
      }),
      {
        select: (r): StatTileData | null => {
          const points = r.series[0]?.points ?? [];
          const last = points.at(-1);
          if (!last) return null;
          return {
            value: last[1],
            previous: points.length > 1 ? points[0]?.[1] : undefined,
            ts: new Date(last[0]),
          };
        },
        isEmpty: (d) => d === null,
        lastUpdated: (d) => d?.ts,
      },
    ),
  Render: lazy(() => import('./render')),
  Editor: lazy(() => import('./editor')),
});
